import React from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  VStack, 
  SimpleGrid, 
  Card, 
  Avatar, 
  Badge, 
  HStack, 
  Button,
  Icon
} from '@chakra-ui/react'
import { FiArrowRight, FiMapPin, FiStar } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'

// Sample practitioner data for demonstration
const samplePractitioners = [
  {
    name: "Dr. Sarah Chen",
    tagline: "Helping you find balance through integrative nutrition",
    specialty: "Nutritionist",
    location: "San Francisco, CA",
    rating: 4.9,
    specialties: ["Weight Management", "Gut Health", "Sports Nutrition"],
    image: null
  },
  {
    name: "Michael Torres",
    tagline: "Transform your mindset, transform your life",
    specialty: "Life Coach",
    location: "Austin, TX",
    rating: 5.0,
    specialties: ["Career Coaching", "Mindfulness", "Goal Setting"],
    image: null
  },
  {
    name: "Dr. Emily Watson",
    tagline: "Evidence-based therapy for lasting change",
    specialty: "Psychologist",
    location: "New York, NY",
    rating: 4.8,
    specialties: ["Anxiety", "Depression", "Trauma Recovery"],
    image: null
  },
  {
    name: "James Park",
    tagline: "Your journey to strength starts here",
    specialty: "Personal Trainer",
    location: "Los Angeles, CA",
    rating: 4.9,
    specialties: ["Strength Training", "Injury Rehab", "Senior Fitness"],
    image: null
  },
  {
    name: "Dr. Lisa Ramirez",
    tagline: "Holistic healing for mind, body, and spirit",
    specialty: "Naturopath",
    location: "Portland, OR",
    rating: 4.7,
    specialties: ["Herbal Medicine", "Detox Programs", "Hormone Balance"],
    image: null
  },
  {
    name: "David Kim",
    tagline: "Breath is the bridge between body and mind",
    specialty: "Yoga Instructor",
    location: "Seattle, WA",
    rating: 5.0,
    specialties: ["Vinyasa", "Meditation", "Breathwork"],
    image: null
  }
]

export const SamplesPage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column">
      <SpotlightHeader />
      
      <Box flex={1}>
        {/* Hero Section */}
        <Box 
          bgGradient="linear(to-br, brand.50, blue.50)" 
          py={{ base: 12, md: 20 }}
        >
          <Container maxW="6xl">
            <VStack gap={6} textAlign="center" maxW="3xl" mx="auto">
              <Badge colorPalette="brand" size="lg" px={4} py={2} borderRadius="full">
                Sample Pages
              </Badge>
              <Heading size={{ base: '3xl', md: '4xl' }} color="gray.900" lineHeight="shorter">
                See Spotlight in Action
              </Heading>
              <Text fontSize="xl" color="gray.700" lineHeight="tall">
                These are sample Spotlight pages. Your page will be generated from your answers and match your carousel ad message.
              </Text>
            </VStack>
          </Container>
        </Box>

        {/* Sample Cards Grid */}
        <Container maxW="7xl" py={{ base: 12, md: 16 }}>
          <VStack gap={12} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
              {samplePractitioners.map((practitioner, idx) => (
                <Card.Root
                  key={idx}
                  borderWidth="1px"
                  borderColor="gray.200"
                  bg="white"
                  borderRadius="2xl"
                  shadow="sm"
                  overflow="hidden"
                  _hover={{ shadow: 'lg', transform: 'translateY(-4px)', borderColor: 'brand.200' }}
                  transition="all 0.3s"
                  cursor="pointer"
                >
                  {/* Header accent bar */}
                  <Box h={2} bgGradient="linear(to-r, brand.400, blue.400)" />
                  
                  <Card.Body p={6}>
                    <VStack align="stretch" gap={4}>
                      {/* Profile Header */}
                      <HStack gap={4}>
                        <Avatar.Root size="xl">
                          <Avatar.Fallback name={practitioner.name} />
                        </Avatar.Root>
                        <VStack align="start" gap={1} flex={1}>
                          <Heading size="md" color="gray.900">{practitioner.name}</Heading>
                          <Badge colorPalette="blue" variant="subtle" fontSize="xs">
                            {practitioner.specialty}
                          </Badge>
                          <HStack gap={1} color="yellow.500" fontSize="sm">
                            <Icon as={FiStar} />
                            <Text fontWeight="semibold">{practitioner.rating}</Text>
                          </HStack>
                        </VStack>
                      </HStack>

                      {/* Tagline */}
                      <Text 
                        fontSize="md" 
                        color="gray.700" 
                        fontStyle="italic"
                        lineHeight="tall"
                      >
                        "{practitioner.tagline}"
                      </Text>

                      {/* Location */}
                      <HStack color="gray.500" fontSize="sm">
                        <Icon as={FiMapPin} />
                        <Text>{practitioner.location}</Text>
                      </HStack>

                      {/* Specialties */}
                      <HStack gap={2} flexWrap="wrap">
                        {practitioner.specialties.map((specialty, sIdx) => (
                          <Badge 
                            key={sIdx} 
                            colorPalette="gray" 
                            variant="subtle" 
                            fontSize="xs"
                            px={2}
                            py={1}
                          >
                            {specialty}
                          </Badge>
                        ))}
                      </HStack>

                      {/* CTA */}
                      <Button 
                        colorPalette="brand" 
                        variant="outline" 
                        size="md" 
                        w="full"
                        mt={2}
                      >
                        View Sample Page
                      </Button>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>

            {/* Info Box */}
            <Box 
              bg="blue.50" 
              borderRadius="xl" 
              p={8} 
              borderLeft="4px solid" 
              borderColor="blue.500"
            >
              <VStack align="start" gap={3}>
                <Heading size="md" color="blue.900">
                  These are sample Spotlight pages
                </Heading>
                <Text color="blue.800" lineHeight="tall">
                  Each Spotlight page is generated from your answers and matches your carousel ad message. 
                  Your page will be uniquely designed to highlight your expertise and convert visitors into clients.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>

        {/* CTA Section */}
        <Box bg="brand.800" color="white" py={{ base: 16, md: 20 }}>
          <Container maxW="4xl">
            <VStack gap={6} textAlign="center">
              <Heading size={{ base: '2xl', md: '3xl' }}>
                Ready to preview your carousel and create your Spotlight page?
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.900" maxW="2xl">
                Get a professionally designed page that showcases your expertise and attracts your ideal clients.
              </Text>
              <Button
                onClick={() => navigate('/for-services/onboarding/basic-setup')}
                size="xl"
                bg="white"
                color="brand.800"
                fontWeight="bold"
                px={8}
                py={6}
                _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                transition="all 0.2s"
              >
                <HStack gap={2}>
                  <Text>Start Getting Clients</Text>
                  <Icon as={FiArrowRight} />
                </HStack>
              </Button>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box 
        position="fixed" 
        bottom={0} 
        left={0} 
        right={0} 
        bg="white" 
        p={4} 
        borderTopWidth="1px" 
        borderColor="gray.200"
        shadow="xl" 
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button 
          w="full" 
          colorPalette="brand" 
          size="lg" 
          onClick={() => navigate('/for-services/onboarding/basic-setup')}
          fontWeight="bold"
        >
          <HStack gap={2}>
            <Text>Start Getting Clients</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}

// Keep the old export name for backwards compatibility
export const SpotlightPageMockup = SamplesPage
